/**
 * Financial Analytics Service
 *
 * Shared utilities for DRE data processing, PROA integration, and metric calculations.
 * Used by CEO Big Numbers Dashboard and Financial Big Numbers page to eliminate code duplication.
 *
 * Features:
 * - DRE data aggregation and consolidation
 * - PROA comparison mapping
 * - Financial metric calculation with variance
 * - Type-safe number conversion
 */

import { trpc } from '@/api/trpc'
import { useGetOfficialProa } from '@/app/financial/proa/hooks/useProaData'
import { FinancialData } from '@/components/financial/financial-config'
import type { AppRouter } from '@ds/ds-api-types/types'
import type { inferRouterOutputs } from '@trpc/server'
import { useMemo } from 'react'

// Type for DRE report output
type RelatorioConsolidadoOutput = inferRouterOutputs<AppRouter>['dre']['getReport'][number]

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Safely converts unknown values to numbers
 * Handles null, undefined, NaN, and type coercion
 */
export function toNum(val: unknown): number {
  if (val === null || val === undefined) return 0
  if (typeof val === 'number') return isNaN(val) ? 0 : val
  const n = Number(val)
  return isNaN(n) ? 0 : n
}

/**
 * Aggregates DRE data rows into consolidated totals
 * Handles absolute values for cost categories
 */
export function sumDreData(data: RelatorioConsolidadoOutput[]) {
  return data.reduce(
    (acc, row) => ({
      faturamento: acc.faturamento + toNum(row.FaturamentoTotal),
      receita: acc.receita + toNum(row.ReceitaBruta),
      custosServicoPrestado: acc.custosServicoPrestado + Math.abs(toNum(row.ServicosPrestados)),
      folha: acc.folha + Math.abs(toNum(row.Folha)),
      despesas: acc.despesas + Math.abs(toNum(row.DespesasOperacionais)),
      impostos: acc.impostos + Math.abs(toNum(row.PIS)) + Math.abs(toNum(row.COFINS)) + Math.abs(toNum(row.ISS)),
      receitaFinanceira: acc.receitaFinanceira + toNum(row.ReceitasFinanceiras),
      lucroLiquido: acc.lucroLiquido + toNum(row.LucroLiquido),
    }),
    {
      faturamento: 0,
      receita: 0,
      custosServicoPrestado: 0,
      folha: 0,
      despesas: 0,
      impostos: 0,
      receitaFinanceira: 0,
      lucroLiquido: 0,
    }
  )
}

/**
 * Creates a financial metric with PROA comparison and variance calculation
 *
 * @param currentValue - Current period value
 * @param prevValue - Previous period value (for variance)
 * @param proaMap - Map of PROA line items to annual targets
 * @param isCost - Whether this is a cost metric (inverts variance logic)
 * @param proaLineItem - Single PROA line item to map
 * @param proaLineItems - Multiple PROA line items to sum (e.g., taxes)
 */
export function createFinancialMetric(
  currentValue: number,
  prevValue: number | null,
  proaMap: Map<string, number>,
  isCost = false,
  proaLineItem?: string,
  proaLineItems?: string[]
): FinancialData {
  // For costs, display as negative
  const real = isCost ? -Math.abs(currentValue) : currentValue

  // Get PROA value from official scenario if available
  let proa = 0
  if (proaLineItems && proaLineItems.length > 0) {
    // Sum multiple PROA lines (e.g., impostos = PIS + COFINS + ISS)
    const summedProa = proaLineItems.reduce((sum, lineItem) => {
      return sum + (proaMap.get(lineItem) ?? 0)
    }, 0)
    proa = isCost ? -Math.abs(summedProa) : summedProa
  } else if (proaLineItem && proaMap.has(proaLineItem)) {
    const proaValue = proaMap.get(proaLineItem) ?? 0
    proa = isCost ? -Math.abs(proaValue) : proaValue
  }

  // Store previous value for display (as negative for costs)
  const previousValue = prevValue !== null
    ? (isCost ? -Math.abs(prevValue) : prevValue)
    : undefined

  // Calculate variance (percentage change)
  let variance = 0
  if (prevValue !== null && prevValue !== 0) {
    if (isCost) {
      // For costs: compare absolute values
      // Shows actual change direction: spending more = positive, spending less = negative
      const currentAbs = Math.abs(currentValue)
      const previousAbs = Math.abs(prevValue)
      variance = ((currentAbs - previousAbs) / previousAbs) * 100
    } else {
      // For revenue/profit: simple growth calculation
      // Higher is better, use actual values (handles negative profits correctly)
      variance = ((currentValue - prevValue) / Math.abs(prevValue)) * 100
    }
  }

  return { real, proa, variance, previousValue }
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Hook for fetching and consolidating DRE data with PROA comparison
 *
 * @param startDate - Period start date (YYYY-MM-DD)
 * @param endDate - Period end date (YYYY-MM-DD)
 * @param previousStartDate - Previous period start date (for variance)
 * @param previousEndDate - Previous period end date (for variance)
 * @param currentYear - Year for PROA fetching
 * @param enabled - Whether to enable data fetching
 */
export function useDREConsolidatedData(
  startDate: string,
  endDate: string,
  previousStartDate: string,
  previousEndDate: string,
  currentYear: number,
  enabled = true
) {
  // Fetch current period DRE data
  const {
    data: dreData = [],
    isLoading: isDreLoading,
    error: dreError,
    refetch: refetchDre,
  } = trpc.dre.getReport.useQuery(
    { dataInicial: new Date(startDate), dataFinal: new Date(endDate) },
    { enabled: enabled && !!startDate && !!endDate, retry: false, staleTime: 5 * 60 * 1000 }
  )

  // Fetch previous period DRE data (for variance)
  const { data: drePreviousPeriod = [] } = trpc.dre.getReport.useQuery(
    { dataInicial: new Date(previousStartDate), dataFinal: new Date(previousEndDate) },
    { enabled: enabled && !!previousStartDate && !!previousEndDate, retry: false, staleTime: 5 * 60 * 1000 }
  )

  // Fetch official PROA for comparison
  const { data: officialProa, isLoading: isProaLoading } = useGetOfficialProa(currentYear)

  // Build PROA map from official scenario
  const proaMap = useMemo(() => {
    const map = new Map<string, number>()
    if (officialProa?.metrics) {
      officialProa.metrics.forEach(proa => {
        map.set(proa.lineItem, proa.annual)
      })
    }
    return map
  }, [officialProa?.metrics])

  // Calculate consolidated data
  const consolidatedData = useMemo(() => {
    if (!dreData.length) return null

    // Sum current period
    const currentTotals = sumDreData(dreData)

    // Sum previous period for comparison
    const previousTotals = drePreviousPeriod.length > 0
      ? sumDreData(drePreviousPeriod)
      : null

    return {
      faturamento: createFinancialMetric(currentTotals.faturamento, previousTotals?.faturamento ?? null, proaMap),
      receita: createFinancialMetric(currentTotals.receita, previousTotals?.receita ?? null, proaMap, false, 'ReceitaBruta'),
      custosServicoPrestado: createFinancialMetric(currentTotals.custosServicoPrestado, previousTotals?.custosServicoPrestado ?? null, proaMap, true, 'ServicosPrestados'),
      folha: createFinancialMetric(currentTotals.folha, previousTotals?.folha ?? null, proaMap, true, 'Folha'),
      despesas: createFinancialMetric(currentTotals.despesas, previousTotals?.despesas ?? null, proaMap, true, 'DespesasOperacionais'),
      impostos: createFinancialMetric(currentTotals.impostos, previousTotals?.impostos ?? null, proaMap, true, undefined, ['PIS', 'COFINS', 'ISS']),
      receitaFinanceira: createFinancialMetric(currentTotals.receitaFinanceira, previousTotals?.receitaFinanceira ?? null, proaMap, false, 'ReceitasFinanceiras'),
      lucroLiquido: createFinancialMetric(currentTotals.lucroLiquido, previousTotals?.lucroLiquido ?? null, proaMap, false, 'LucroLiquido'),
    }
  }, [dreData, drePreviousPeriod, proaMap])

  return {
    consolidatedData,
    dreData,
    drePreviousPeriod,
    officialProa,
    isLoading: isDreLoading || isProaLoading,
    error: dreError,
    refetch: refetchDre,
  }
}

/**
 * Hook for building monthly chart data from DRE data
 */
export function useMonthlyChartData(dreData: RelatorioConsolidadoOutput[]) {
  return useMemo(() => {
    const byMonth: Record<string, RelatorioConsolidadoOutput> = {}
    dreData.forEach((row) => {
      const key = `${row.Ano}-${String(row.Mes).padStart(2, '0')}`
      byMonth[key] = row
    })

    const sortedMonths = Object.keys(byMonth).sort()

    return sortedMonths.map(monthKey => {
      const row = byMonth[monthKey]
      const [year, month] = monthKey.split('-').map(Number)
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
      return {
        month: `${monthNames[month - 1]}/${year}`,
        receita: toNum(row?.ReceitaBruta),
        custos: Math.abs(toNum(row?.ServicosPrestados)) +
          Math.abs(toNum(row?.Folha)) +
          Math.abs(toNum(row?.MaoDeObra)) +
          Math.abs(toNum(row?.DespesasOperacionais)) +
          Math.abs(toNum(row?.PIS)) +
          Math.abs(toNum(row?.COFINS)) +
          Math.abs(toNum(row?.ISS))
      }
    })
  }, [dreData])
}
